import React, { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { ILoadDetailsState } from 'apps/fleet-planner/types/loadDetails'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { InlineMessage, INLINE_MESSAGE_VARIANTS, Spinner } from 'transfix-ui'
import { SmsPromptSettingsModalForm } from './SmsPromptSettingsModalForm'
import { TSmsPromptSettingsModalSchema } from './SmsPromptSettingsModalSchema'
import { useShipmentQuery } from './graphql/shipment'
import { useUpdateDriverSmsSettingsMutation } from './graphql/updateDriverSmsSettings'
import { useSmsPromptSettingsModalForm } from './useSmsPromptSettingsModalForm'

interface ISmsPromptSettingsModalProps extends Omit<IModalBaseProps, 'modalType'> {}

const useSmsPromptSettingsModalStyles = (isMobile?: boolean) =>
  makeStyles(({ spacing }) => ({
    body: {
      marginTop: isMobile ? 0 : spacing(2),
    },
  }))

export const SmsPromptSettingsModal = ({
  onClose,
  open = true,
  ...props
}: ISmsPromptSettingsModalProps) => {
  const { load } = useSelector<IStore, ILoadDetailsState>(loadDetailsSelector)
  const { id: shipmentId } = load || {}

  const [updateDriverSmsSettings] = useUpdateDriverSmsSettingsMutation()

  const { data, loading } = useShipmentQuery({
    variables: {
      id: shipmentId || '',
    },
  })

  const { shipment } = data || {}
  const { drivers } = shipment || {}
  const [selectedDriver] = drivers || []
  const { shipmentDrivers } = selectedDriver || {}

  const enableUpdatesForAllLoads = selectedDriver?.smsStatusUpdatesEnabled || false
  const enabledUpdatesForShipment = shipmentDrivers?.some(
    ({ smsStatusUpdatesEnabled }) => smsStatusUpdatesEnabled
  )
  const enableUpdates = enableUpdatesForAllLoads || enabledUpdatesForShipment

  const methods = useSmsPromptSettingsModalForm({
    defaultValues: {
      enableUpdates,
      enableUpdatesForAllLoads,
    },
  })

  const { errors, handleSubmit } = methods || {}

  useEffect(() => {
    if (!data) return
    methods.reset({
      cellPhone: selectedDriver?.cellPhone,
      enableUpdates,
      enableUpdatesForAllLoads,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const onSubmit = ({
    cellPhone,
    enableUpdates,
    enableUpdatesForAllLoads,
  }: TSmsPromptSettingsModalSchema) => {
    if (!selectedDriver?.id) return onClose()
    return updateDriverSmsSettings({
      variables: {
        input: {
          driverId: selectedDriver.id,
          smsStatusUpdatesEnabled: enableUpdates && enableUpdatesForAllLoads,
          shipmentSmsStatusUpdatesEnabled: enableUpdates && !enableUpdatesForAllLoads,
          cellPhone,
          shipmentId,
        },
      },
    }).finally(() => {
      onClose()
    })
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: onClose,
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: handleSubmit(onSubmit),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const { isMobile } = useBreakpointContext()
  const classes = useSmsPromptSettingsModalStyles(isMobile)()

  return (
    <ModalBase
      dataTest='SmsPromptSettingsModal'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={actions}
      title='SMS Prompt Settings'
      bodyClassName={classes.body}
      onClose={onClose}
      {...props}>
      {loading ? (
        <Spinner />
      ) : (
        <FormProvider {...methods}>
          <div>
            {Object.values(errors).map(value => (
              <div key={value.message}>
                <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={value.message} />
              </div>
            ))}
          </div>
          <p>
            We&apos;ll automatically text your driver a timely reminder at every stop to update
            their status. You&apos;ll recieve an email when they arrive and depart each pickup and
            dropoff.
          </p>
          <SmsPromptSettingsModalForm selectedDriver={selectedDriver} />
        </FormProvider>
      )}
    </ModalBase>
  )
}
